<template>
  <AppDrawer
    v-model="open"
    :title="t('promo.rebate.rateDetailTitle')"
    padded
    body-padding="14px 14px 48px"
    @back="emit('close')"
  >
    <div class="sub-body">
      <div class="filters">
        <div class="filter-dropdown" :class="{ open: typeOpen }">
          <button type="button" class="filter-chip" @click="toggleType">
            {{ t('promo.rebate.filterGameType') }} · <span class="provider-name">{{ selectedTypeName }}</span>
            <span class="chev">▾</span>
          </button>
          <ul v-if="typeOpen" class="filter-options">
            <li
              v-for="item in typeOptions"
              :key="item.code"
              :class="{ active: item.code === selectedTypeCode }"
              @click="selectType(item.code)"
            >
              {{ item.name }}
            </li>
          </ul>
        </div>

        <div class="filter-dropdown" :class="{ open: providerOpen }">
          <button type="button" class="filter-chip" @click="toggleProvider">
            {{ t('promo.rebate.filterVendor') }} · <span class="provider-name">{{ selectedVendorName }}</span>
            <span class="chev">▾</span>
          </button>
          <ul v-if="providerOpen" class="filter-options">
            <li :class="{ active: selectedVendorCode === '' }" @click="selectVendor('')">
              {{ t('promo.rebate.baseMatrix') }}
            </li>
            <li
              v-for="item in vendorOptions"
              :key="item.code"
              :class="{ active: item.code === selectedVendorCode }"
              @click="selectVendor(item.code)"
            >
              {{ item.name }}
            </li>
          </ul>
        </div>
      </div>

      <div class="section" style="margin-top: 14px">
        <div v-if="loading" class="loading-hint">{{ t('promo.rebate.loading') }}</div>
        <div v-else class="lvl-table-wrap">
          <table id="rd-table" class="lvl-table">
            <thead>
              <tr>
                <th>{{ t('promo.rebate.vipLevel') }}</th>
                <th>{{ t('promo.rebate.totalDeposit') }}</th>
                <th>{{ t('promo.rebate.rebateRate') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.level" :class="{ on: row.current }">
                <td>V{{ row.level }}<span v-if="row.current"> ←</span></td>
                <td>{{ row.threshold }}</td>
                <td>{{ row.rate }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p class="rd-note">
        {{ t('promo.rebate.rateNote') }}
      </p>
    </div>
  </AppDrawer>
</template>

<script setup lang="ts">
import { fetchRakebackRates, fetchVipSalaryPanel } from '~/api/promo'
import { formatBaseAmt } from '~/utils/transfer/money'
import type { RakebackRateOptionDto, RakebackRatesDto, VipSalaryLevelDto } from '~/types/api/promo'
import AppDrawer from '~/components/AppDrawer.vue'

const { t } = useI18n()

const props = defineProps<{
  modelValue: boolean
  initialGameType?: string
}>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
}>()

const userStore = useUserStore()

const open = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const loading = ref(false)
const data = ref<RakebackRatesDto | undefined>(undefined)
const vipLevels = ref<VipSalaryLevelDto[]>([])

const typeOptions = computed<RakebackRateOptionDto[]>(() => data.value?.game_types ?? [])
const vendorOptions = computed<RakebackRateOptionDto[]>(() => data.value?.vendors ?? [])

const selectedTypeCode = ref('')
const selectedVendorCode = ref('')

const selectedTypeName = computed(
  () => typeOptions.value.find(it => it.code === selectedTypeCode.value)?.name ?? t('promo.rebate.all'),
)
const selectedVendorName = computed(() => {
  if (!selectedVendorCode.value) return t('promo.rebate.baseMatrix')
  return vendorOptions.value.find(it => it.code === selectedVendorCode.value)?.name ?? t('promo.rebate.baseMatrix')
})

const currentLevel = computed(() => data.value?.current_level ?? 0)
const maxLevel = computed(() => data.value?.max_level ?? 50)

const toNumber = (value: string) => Number(value) || 0

const thresholdMap = computed(() => {
  const map = new Map<number, string>()
  vipLevels.value.forEach(l => map.set(l.level, l.threshold))
  return map
})

const activeGroup = computed(() => {
  if (!data.value) return undefined
  return data.value.groups.find(g => g.game_type === selectedTypeCode.value) ?? data.value.groups[0]
})

const rows = computed(() => {
  const segments = activeGroup.value?.segments ?? []
  const result: { level: number; threshold: string; rate: string; current: boolean }[] = []
  for (const seg of segments) {
    const from = seg.level_from
    const to = Math.min(seg.level_to, maxLevel.value)
    const ratePct = (toNumber(seg.rate) * 100).toFixed(2)
    for (let lv = from; lv <= to; lv++) {
      const raw = toNumber(thresholdMap.value.get(lv) ?? '0')
      result.push({
        level: lv,
        threshold: formatBaseAmt(raw, userStore.baseCurrency),
        rate: ratePct,
        current: lv === currentLevel.value,
      })
    }
  }
  return result
})

const fetchData = async (game_type?: string, vendor_code?: string) => {
  loading.value = true
  try {
    const params: { game_type?: string; vendor_code?: string } = {}
    if (game_type) params.game_type = game_type
    if (vendor_code) params.vendor_code = vendor_code
    const [ratesResult, salaryResult] = await Promise.all([
      fetchRakebackRates(params),
      vipLevels.value.length ? Promise.resolve(null) : fetchVipSalaryPanel(),
    ])
    data.value = ratesResult
    if (salaryResult) {
      vipLevels.value = salaryResult.levels ?? []
    }
  } catch (error) {
    console.error('Fetch rakeback rates failed:', error)
  } finally {
    loading.value = false
  }
}

watch(
  () => props.modelValue,
  visible => {
    if (visible) {
      selectedTypeCode.value = props.initialGameType ?? ''
      selectedVendorCode.value = ''
      fetchData(selectedTypeCode.value || undefined, undefined)
    }
  },
)

const typeOpen = ref(false)
const providerOpen = ref(false)
const toggleType = () => {
  typeOpen.value = !typeOpen.value
  providerOpen.value = false
}
const toggleProvider = () => {
  providerOpen.value = !providerOpen.value
  typeOpen.value = false
}
const selectType = (code: string) => {
  selectedTypeCode.value = code
  selectedVendorCode.value = ''
  typeOpen.value = false
  fetchData(code || undefined, undefined)
}
const selectVendor = (code: string) => {
  selectedVendorCode.value = code
  providerOpen.value = false
  fetchData(selectedTypeCode.value || undefined, code || undefined)
}
</script>

<style scoped lang="scss">
.filters {
  display: flex;
  gap: 8px;
  margin-top: 14px;
}

.filter-dropdown {
  position: relative;
}

.filter-chip {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 13px;
  border-radius: var(--r-sm);
  background: var(--surface);
  border: 1px solid var(--hairline);
  color: var(--text);
  font-size: 12.5px;
  font-weight: 700;

  .provider-name {
    color: var(--primary);
  }
}

.filter-dropdown.open .filter-chip {
  border-color: var(--primary);
  color: var(--primary);
}

.chev {
  font-style: normal;
  font-size: 10px;
  color: var(--muted);
  transition: transform 0.18s;
}

.filter-dropdown.open .chev {
  transform: rotate(180deg);
}

.filter-options {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  min-width: 120px;
  padding: 6px;
  border-radius: var(--r-md);
  background: var(--surface);
  border: 1px solid var(--hairline);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45);
  opacity: 0;
  transform: translateY(-6px);
  pointer-events: none;
  transition:
    opacity 0.16s,
    transform 0.16s;

  &.open,
  .filter-dropdown.open & {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }

  li {
    display: block;
    width: 100%;
    text-align: left;
    padding: 9px 12px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
  }

  li.active {
    color: var(--primary);
    background: rgba(var(--primary-rgb), 0.12);
    font-weight: 800;
  }
}

.lvl-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--hairline);
  border-radius: 14px;
  -webkit-overflow-scrolling: touch;
}

.lvl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  white-space: nowrap;

  th {
    background: var(--surface2);
    color: var(--muted);
    font-weight: 700;
    padding: 10px 12px;
    text-align: right;
  }

  th:first-child {
    text-align: left;
  }

  tbody td {
    padding: 11px 12px;
    text-align: right;
    border-top: 1px solid var(--hairline);
  }

  tbody td:first-child {
    text-align: left;
    font-weight: 800;
  }

  tr.on td {
    color: var(--primary);
    font-weight: 800;
    background: rgba(var(--primary-rgb), 0.12);
  }
}

.lvl-table th:first-child,
.lvl-table td:first-child {
  text-align: left;
  font-weight: 800;
}

.loading-hint {
  text-align: center;
  padding: 32px 0;
  font-size: 13px;
  color: var(--muted);
}

.rd-note {
  font-size: 12px;
  color: var(--muted);
}
</style>
